import { useMemo } from 'react'
import { ApolloClient, InMemoryCache, ApolloLink, HttpLink } from '@apollo/client'
import { withScalars } from 'apollo-link-scalars'
import { buildClientSchema, IntrospectionQuery } from 'graphql'
import { DateTime } from 'luxon'

import useConfig from '@/gf/hooks/useConfig'
import MoneyM from '@/gf/modules/Money'
import { createErrorLink } from '@/gf/utils/apolloErrorLink'
import { createTraceContextLink } from '@/gf/utils/apolloTraceLink'
import GqlClientContext from '@/suppliers/contexts/GqlClient'
import introspectionResult from '@/suppliers/_gen/introspection.json'

import { Money } from '@/types'
import { skipPollWhileHidden } from '@/gf/modules/PollWhileVisible'

const schema = buildClientSchema(introspectionResult as unknown as IntrospectionQuery)

const typesMap = {
  NaiveDateTime: {
    serialize: (parsed: unknown) => (parsed instanceof DateTime ? parsed.toISO() : null),
    parseValue: (raw: unknown) => {
      if (!raw) return null // if for some reason we want to treat empty string as null, for example
      return DateTime.fromISO(raw as string, { zone: 'utc' }).toLocal()
    },
  },
  Money: {
    serialize: (parsed: unknown) => (parsed !== null ? MoneyM.toPayloadV2(parsed as Money) : null),
    parseValue: (raw: unknown) => {
      if (!raw) return null // if for some reason we want to treat empty string as null, for example
      return MoneyM.fromPayloadV2(raw as string)
    },
  },
  Decimal: {
    serialize: (parsed: unknown) =>
      typeof parsed === 'number' ? `${parsed}` : typeof parsed === 'string' ? parsed : null,
    parseValue: (raw: unknown) => {
      if (!raw) return null // if for some reason we want to treat empty string as null, for example
      return parseFloat(raw as string)
    },
  },
}

const GqlClientProvider = ({ children }: { children: React.ReactNode }) => {
  const config = useConfig()

  const client = useMemo(
    () =>
      new ApolloClient({
        link: ApolloLink.from([
          createErrorLink(),
          createTraceContextLink(),
          withScalars({ schema, typesMap }),
          new HttpLink({ uri: `${config.suppliersUrl}/gql/next` }),
        ]),
        cache: new InMemoryCache(),
        credentials: 'include',
        defaultOptions: {
          watchQuery: { fetchPolicy: 'no-cache', skipPollAttempt: skipPollWhileHidden },
          query: { fetchPolicy: 'no-cache' },
          mutate: { fetchPolicy: 'no-cache' },
        },
      }),
    []
  )

  return <GqlClientContext.Provider value={client}>{children}</GqlClientContext.Provider>
}

export default GqlClientProvider
